import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchResearchQuestions,
  fetchResearchRecommendations,
  getResearchDraft,
  saveResearchDraft,
  submitResearchRequest,
  fetchGatewayCatalog,
} from '@/features/request-gateway/api';
import type {
  CatalogResolved,
  GatewayDirection,
  GatewayService,
  ResearchQuestion,
  ResearchSubmitResult,
} from '@/features/request-gateway/types';
import {
  applyPreliminaryAccept,
  applyPreliminaryClear,
  getMaterialsFileQuestions,
  getVisibleQuestions,
  answerText,
  isAnswerFilled,
  listMissingRequiredAnswers,
  normalizeQuestionOptions,
  seedAnswersFromPreliminary,
} from '@/features/request-gateway/researchWizardUtils';
import { useAuth } from '@/hooks/useAuth';
import { QuestionCard, chipStatus, type AnswerDecision } from './R54QuestionCard';
import '../r54-request.css';

interface MyRequestsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenGateway?: () => void;
  onOpenProjects?: () => void;
  onOpenOpportunities?: () => void;
}

const STEPS = ['Услуга', 'Сведения', 'Материалы', 'Предварительные ответы', 'Проверка'];
const PATHS = [
  '/app/requests/new/service',
  '/app/requests/new/details',
  '/app/requests/new/materials',
  '/app/requests/new/preliminary',
  '/app/requests/new/review',
];
const TITLES = [
  'Новая заявка · услуга',
  'Новая заявка · сведения',
  'Новая заявка · материалы',
  'Новая заявка · предварительные ответы',
  'Новая заявка · проверка',
];

const ALLOWED_EXT = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'csv', 'txt', 'zip']);
const MAX_FILES = 10;

interface LocalFile {
  file_id: string;
  name: string;
  size: number;
  type: string;
  status: 'SAFE_LOCAL';
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : 'Не удалось выполнить запрос.';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function fileExt(name: string): string {
  return name.split('.').pop()?.toLowerCase() || '';
}

export const MyRequestsOverlay: React.FC<MyRequestsOverlayProps> = ({
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();
  const catalogRole = user?.roles?.includes('SYSTEM_OWNER') ? 'SYSTEM_OWNER' : 'STUDENT';
  const [catalog, setCatalog] = useState<CatalogResolved | null>(null);
  const [directionId, setDirectionId] = useState<string | null>(null);
  const [service, setService] = useState<GatewayService | null>(null);
  const [step, setStep] = useState(1);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<ResearchQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [decisions, setDecisions] = useState<Record<string, AnswerDecision>>({});
  const [files, setFiles] = useState<LocalFile[]>([]);
  const [blockedFile, setBlockedFile] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [declaration, setDeclaration] = useState(false);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState<ResearchSubmitResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceIndex = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let live = true;
    fetchGatewayCatalog(catalogRole)
      .then((data) => {
        if (!live) return;
        setCatalog(data);
        const first = data.directions?.[0]?.direction_id || null;
        setDirectionId((current) => current || first);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [catalogRole, isOpen]);

  const directions = catalog?.directions || [];
  const services = catalog?.services || [];
  const activeDirection: GatewayDirection | null =
    directions.find((item) => item.direction_id === directionId) || directions[0] || null;
  const directionServices = useMemo(
    () => services.filter((item) => item.direction_id === activeDirection?.direction_id),
    [services, activeDirection?.direction_id],
  );
  const visibleQuestions = useMemo(
    () => getVisibleQuestions(questions, answers),
    [questions, answers],
  );
  const fileQuestions = useMemo(
    () => getMaterialsFileQuestions(visibleQuestions),
    [visibleQuestions],
  );

  const counts = useMemo(() => {
    const tally = { SOURCE_CONFIRMED: 0, NEEDS_CONFIRMATION: 0, USER_CONFIRMED: 0, CONFLICT_REVIEW: 0, changed: 0 };
    for (const question of visibleQuestions) {
      const status = chipStatus(question, answers, decisions[question.id]);
      tally[status] += 1;
      if (decisions[question.id] === 'edited') tally.changed += 1;
    }
    return tally;
  }, [visibleQuestions, answers, decisions]);

  const loadService = async (item: GatewayService) => {
    setLoading(true);
    setNotice('');
    try {
      const payload = await fetchResearchQuestions(item.service_id, catalogRole);
      const nextQuestions = normalizeQuestionOptions(payload.questions || []);
      let nextAnswers: Record<string, unknown> = {};
      let nextDraftId: string | null = null;
      let nextKey: string = crypto.randomUUID();
      let nextFiles: LocalFile[] = [];
      if (typeof getResearchDraft === 'function') {
        try {
          const draft = await getResearchDraft(catalogRole);
          if (draft?.service_id === item.service_id) {
            nextAnswers = draft.answers || {};
            nextDraftId = draft.id && draft.id !== 'new' ? draft.id : null;
            nextKey = draft.idempotency_key || nextKey;
            nextFiles = (draft.files || []).map((file) => ({
              file_id: file.file_id,
              name: file.name,
              size: file.size,
              type: fileExt(file.name).toUpperCase() || 'FILE',
              status: 'SAFE_LOCAL' as const,
            }));
          }
        } catch {
          nextAnswers = {};
        }
      }
      setQuestions(nextQuestions);
      setAnswers(seedAnswersFromPreliminary(nextQuestions, nextAnswers));
      setDecisions({});
      setFiles(nextFiles);
      setDraftId(nextDraftId);
      setIdempotencyKey(nextKey);
      setDeclaration(false);
      setSubmitted(null);
      setRecommendations([]);
      setStep(2);
    } catch (error) {
      setNotice(errorText(error));
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    if (!service || typeof saveResearchDraft !== 'function') {
      setNotice(service ? 'Черновик сохранится после загрузки вопросов услуги.' : 'Сначала выберите услугу.');
      return;
    }
    try {
      const saved = await saveResearchDraft(
        {
          id: draftId || undefined,
          step: Math.min(step, 5) as 0 | 1 | 2 | 3 | 4 | 5,
          start_mode: 'SCRATCH',
          service_id: service.service_id,
          direction_id: service.direction_id,
          answers,
          files: files.map(({ file_id, name, size }) => ({ file_id, name, size })),
          idempotency_key: idempotencyKey || undefined,
        },
        catalogRole,
      );
      if (saved?.id && saved.id !== 'new') setDraftId(saved.id);
      setNotice('Черновик сохранён.');
    } catch (error) {
      setNotice(errorText(error));
    }
  };

  const addFiles = (list: File[]) => {
    const next = [...files];
    let blocked: string | null = null;
    for (const file of list) {
      const ext = fileExt(file.name);
      if (!ALLOWED_EXT.has(ext)) {
        blocked = file.name;
        continue;
      }
      if (file.size > 50 * 1024 * 1024) {
        setNotice(`Файл слишком большой: ${file.name}. Лимит 50 МБ.`);
        continue;
      }
      const row: LocalFile = {
        file_id: `local-${crypto.randomUUID()}`,
        name: file.name,
        size: file.size,
        type: ext.toUpperCase(),
        status: 'SAFE_LOCAL',
      };
      const index = replaceIndex.current;
      if (index != null && next[index]) next[index] = row;
      else if (next.length < MAX_FILES) next.push(row);
      else setNotice(`Можно прикрепить не больше ${MAX_FILES} файлов.`);
      replaceIndex.current = null;
    }
    setBlockedFile(blocked);
    setFiles(next);
  };

  const decide = (question: ResearchQuestion, decision: AnswerDecision) => {
    if (decision === 'accepted') {
      if (!question.preliminary_answer) {
        setNotice('Предварительного ответа нет — заполните значение и подтвердите его.');
        return;
      }
      setAnswers((current) => applyPreliminaryAccept(question, current));
    }
    if (decision === 'edited') {
      document.getElementById(`r54-q-${question.id}`)?.focus();
    }
    if (decision === 'cleared') {
      setAnswers((current) => applyPreliminaryClear(question, current));
    }
    setDecisions((current) => ({ ...current, [question.id]: decision }));
    setNotice('');
  };

  const confirmAnswer = (question: ResearchQuestion) => {
    const current = answers[question.id];
    if (!isAnswerFilled(current)) {
      if (!question.preliminary_answer) {
        setNotice('Сначала заполните уникальное значение этой услуги.');
        return;
      }
      setAnswers((prev) => applyPreliminaryAccept(question, prev));
      setDecisions((prev) => ({ ...prev, [question.id]: 'accepted' }));
      setNotice('');
      return;
    }
    const matches = Boolean(question.preliminary_answer) && answerText(current) === answerText(question.preliminary_answer);
    setDecisions((prev) => ({ ...prev, [question.id]: matches ? 'accepted' : 'edited' }));
    setNotice('');
  };

  const openReview = async () => {
    if (!service) return;
    setStep(5);
    if (typeof fetchResearchRecommendations !== 'function') return;
    try {
      const result = await fetchResearchRecommendations(
        { service_id: service.service_id, answers },
        catalogRole,
      );
      setRecommendations(result.recommendations || []);
    } catch {
      setRecommendations([]);
    }
  };

  const submit = async () => {
    if (!service) return;
    const missing = listMissingRequiredAnswers(questions, answers, files);
    if (missing.length) {
      setNotice(missing[0]);
      return;
    }
    if (counts.CONFLICT_REVIEW > 0) {
      setNotice('Снимите конфликты перед созданием заявки.');
      return;
    }
    if (!declaration) {
      setNotice('Подтвердите декларацию перед созданием заявки.');
      return;
    }
    setLoading(true);
    try {
      const result = await submitResearchRequest(
        {
          draft_id: draftId || undefined,
          service_id: service.service_id,
          direction_id: service.direction_id,
          title: service.title,
          applicant_name: user?.fullName,
          answers,
          files: files.map(({ file_id, name, size }) => ({ file_id, name, size })),
          declaration_confirmed: true,
          idempotency_key: idempotencyKey || crypto.randomUUID(),
        },
        catalogRole,
      );
      setSubmitted(result);
      setNotice(result.message || 'Заявка создана.');
    } catch (error) {
      setNotice(errorText(error));
    } finally {
      setLoading(false);
    }
  };

  const continueNext = () => {
    if (step === 1) {
      if (!service) {
        setNotice('Сначала выберите услугу выбранного направления.');
        return;
      }
      void loadService(service);
      return;
    }
    setNotice('');
    if (step === 4) {
      void openReview();
      return;
    }
    if (step === 5) {
      void submit();
      return;
    }
    setNotice('');
    setStep((current) => Math.min(5, current + 1));
  };

  if (!isOpen) return null;

  const title = TITLES[step - 1];
  const path = PATHS[step - 1];

  return (
    <div className="r54-root">
      <header className="r54-route-top">
        <button type="button" className="r54-back" onClick={step === 1 ? onClose : () => setStep((current) => current - 1)}>← Назад</button>
        <div className="r54-route-title">
          <small>{path} · Обучающийся</small>
          <strong>{title}</strong>
        </div>
        <div className="r54-route-actions">
          <button type="button" className="r54-quiet" disabled={step === 1} onClick={() => setStep((current) => Math.max(1, current - 1))}>← Назад</button>
          <button type="button" className="r54-quiet" onClick={() => void saveDraft()}>Сохранить черновик</button>
          <button type="button" className="r54-quiet" onClick={onClose}>Выйти и продолжить позже</button>
          <button type="button" className="r54-action" disabled={loading || Boolean(submitted)} onClick={continueNext}>
            {step === 5 ? 'Создать заявку' : 'Продолжить →'}
          </button>
          <button type="button" className="r54-icon-button" title="Профиль">СЖ</button>
          <button type="button" className="r54-quiet" onClick={onClose}>Закрыть ×</button>
        </div>
      </header>
      <main className="r54-route-body">
        <div className="r54-page">
          <StepHero
            step={step}
            service={service}
            direction={activeDirection}
            directions={directions.length}
            services={services.length}
            questionCount={visibleQuestions.length}
            missingUnique={visibleQuestions.filter((item) => !item.preliminary_answer && !isAnswerFilled(answers[item.id])).length}
            submitted={submitted}
          />
          <section className="r54-wizard-steps">
            {STEPS.map((label, index) => (
              <div key={label} className={`r54-wizard-step${index + 1 === step ? ' active' : ''}${index + 1 < step ? ' done' : ''}`}>
                <b>{index + 1}</b>
                <span>{label}</span>
              </div>
            ))}
          </section>
          {step === 1 ? (
            <ServiceStep
              directions={directions}
              services={services}
              activeDirection={activeDirection}
              directionServices={directionServices}
              service={service}
              onDirection={(id) => {
                setDirectionId(id);
                setService((current) => (current?.direction_id === id ? current : null));
                setNotice('');
              }}
              onService={(item) => {
                setService(item);
                setNotice('');
              }}
            />
          ) : null}
          {step === 2 ? (
            <section className="r54-question-list">
              {loading ? <p className="r54-note">Загрузка вопросов услуги…</p> : null}
              {visibleQuestions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  serviceCode={service?.display_code || ''}
                  value={answers[question.id]}
                  decision={decisions[question.id]}
                  onChange={(value) => {
                    setAnswers((current) => ({ ...current, [question.id]: value }));
                    setDecisions((current) => ({ ...current, [question.id]: 'edited' }));
                  }}
                  onDecision={(decision) => decide(question, decision)}
                  onConfirm={() => confirmAnswer(question)}
                />
              ))}
            </section>
          ) : null}
          {step === 3 ? (
            <MaterialsStep
              files={files}
              blockedFile={blockedFile}
              fileQuestions={fileQuestions}
              inputRef={fileInputRef}
              onPick={() => {
                replaceIndex.current = null;
                fileInputRef.current?.click();
              }}
              onFiles={addFiles}
              onReplace={(index) => {
                replaceIndex.current = index;
                fileInputRef.current?.click();
              }}
              onDelete={(index) => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
            />
          ) : null}
          {step === 4 ? (
            <>
              <section className="r54-grid-4">
                <article className="r54-stat"><small>Источник подтверждён</small><b>{counts.SOURCE_CONFIRMED}</b></article>
                <article className="r54-stat" style={{ ['--r54-accent' as string]: 'var(--r54-gold)' }}><small>Требует подтверждения</small><b>{counts.NEEDS_CONFIRMATION}</b></article>
                <article className="r54-stat" style={{ ['--r54-accent' as string]: '#5ed6a0' }}><small>Подтверждено пользователем</small><b>{counts.USER_CONFIRMED}</b></article>
                <article className="r54-stat" style={{ ['--r54-accent' as string]: '#ff7a7a' }}><small>Требует уточнения</small><b>{counts.CONFLICT_REVIEW}</b></article>
              </section>
              <section className="r54-question-list">
                {visibleQuestions.map((question, index) => (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    index={index}
                    serviceCode={service?.display_code || ''}
                    value={answers[question.id]}
                    decision={decisions[question.id]}
                    onChange={(value) => {
                      setAnswers((current) => ({ ...current, [question.id]: value }));
                      setDecisions((current) => ({ ...current, [question.id]: 'edited' }));
                    }}
                    onDecision={(decision) => decide(question, decision)}
                    onConfirm={() => confirmAnswer(question)}
                  />
                ))}
              </section>
            </>
          ) : null}
          {step === 5 && service ? (
            <ReviewStep
              service={service}
              direction={activeDirection}
              counts={counts}
              recommendations={recommendations}
              missing={listMissingRequiredAnswers(questions, answers, files)}
              declaration={declaration}
              submitted={submitted}
              onDeclaration={setDeclaration}
            />
          ) : null}
          {notice ? <p className="r54-note">{notice}</p> : null}
        </div>
      </main>
    </div>
  );
};

const StepHero: React.FC<{
  step: number;
  service: GatewayService | null;
  direction: GatewayDirection | null;
  directions: number;
  services: number;
  questionCount: number;
  missingUnique: number;
  submitted: ResearchSubmitResult | null;
}> = ({ step, service, directions, services, questionCount, missingUnique, submitted }) => {
  if (submitted) {
    return (
      <section className="r54-page-hero">
        <div>
          <span className="r54-eyebrow">Заявка создана</span>
          <h1>{submitted.reference_code}</h1>
          <p>{submitted.message}</p>
        </div>
        <div className="r54-badges"><span className="r54-chip good">{submitted.status}</span></div>
      </section>
    );
  }
  if (step === 1) {
    return (
      <section className="r54-page-hero">
        <div>
          <span className="r54-eyebrow">NEW REQUEST · STEP 1</span>
          <h1>Выберите направление и услугу</h1>
          <p>Сначала выберите одно из доступных направлений. Затем справа или ниже появятся только разрешённые услуги выбранного направления.</p>
        </div>
        <div className="r54-badges">
          <span className="r54-chip info">{directions} НАПРАВЛЕНИЙ</span>
          <span className="r54-chip info">{services} УСЛУГ</span>
          <span className="r54-chip info">5 ШАГОВ</span>
        </div>
      </section>
    );
  }
  if (step === 2) {
    return (
      <section className="r54-page-hero">
        <div>
          <span className="r54-eyebrow">NEW REQUEST · STEP 2 · {service?.display_code}</span>
          <h1>Сведения · {service?.title}</h1>
          <p>Универсальная страница загрузила {questionCount} относящихся к услуге вопросов. Старые ответы другой услуги не смешиваются; уникальные сведения оставлены пустыми.</p>
        </div>
        <div className="r54-badges"><span className="r54-chip info">{questionCount} ВОПРОСОВ</span></div>
      </section>
    );
  }
  if (step === 3) {
    return (
      <section className="r54-page-hero">
        <div>
          <span className="r54-eyebrow">NEW REQUEST · STEP 3</span>
          <h1>Материалы и файлы</h1>
          <p>Файлы остаются в черновике заявки. Принимаются распространённые безопасные форматы, исполняемые файлы отклоняются.</p>
        </div>
        <div className="r54-badges"><span className="r54-chip info">Допустимые форматы</span><span className="r54-chip info">MAX {MAX_FILES}</span></div>
      </section>
    );
  }
  if (step === 4) {
    return (
      <section className="r54-page-hero">
        <div>
          <span className="r54-eyebrow">NEW REQUEST · STEP 4 · {service?.display_code}</span>
          <h1>Предварительные ответы · {service?.title}</h1>
          <p>Показаны {questionCount} записей выбранной услуги. Их можно принять, изменить, очистить, подтвердить или отправить на проверку конфликта.</p>
        </div>
        <div className="r54-badges"><span className="r54-chip info">{service?.display_code} · {questionCount} Показано</span></div>
      </section>
    );
  }
  return (
    <section className="r54-page-hero">
      <div>
        <span className="r54-eyebrow">NEW REQUEST · STEP 5 · {service?.display_code}</span>
        <h1>Итоговая проверка · {service?.title}</h1>
        <p>Проверьте принятые, изменённые и неподтверждённые ответы, источники, предупреждения и уникальные пустые сведения выбранной услуги.</p>
      </div>
      <div className="r54-badges">
        <span className={`r54-chip ${missingUnique ? 'warn' : 'good'}`}>{missingUnique ? `${missingUnique} ответов нужно заполнить` : 'Готово'}</span>
      </div>
    </section>
  );
};

const ServiceStep: React.FC<{
  directions: GatewayDirection[];
  services: GatewayService[];
  activeDirection: GatewayDirection | null;
  directionServices: GatewayService[];
  service: GatewayService | null;
  onDirection: (id: string) => void;
  onService: (item: GatewayService) => void;
}> = ({ directions, services, activeDirection, directionServices, service, onDirection, onService }) => (
  <>
    <section className="r54-panel">
      <header>
        <div>
          <h2>Направления</h2>
          <p>Выберите услугу, заполните сведения и приложите материалы.</p>
        </div>
        <span className="r54-chip info">{directions.length}</span>
      </header>
      <div className="r521-direction-tabs">
        {directions.map((direction) => {
          const count = services.filter((item) => item.direction_id === direction.direction_id).length;
          const selected = direction.direction_id === activeDirection?.direction_id;
          return (
            <button
              key={direction.direction_id}
              type="button"
              className={`r521-direction-tab${selected ? ' selected' : ''}`}
              onClick={() => onDirection(direction.direction_id)}
            >
              <b>{direction.display_code}</b>
              <span>{direction.title}</span>
              <small>{count} услуг</small>
            </button>
          );
        })}
      </div>
    </section>
    <section className="r54-panel">
      <header>
        <div>
          <h2>Услуги направления {activeDirection?.display_code || ''}</h2>
          <p>{activeDirection?.title || 'Канонический каталог'}</p>
        </div>
        <span className="r54-chip info">{directionServices.length}</span>
      </header>
      <div className="r521-service-cards">
        {directionServices.map((item) => (
          <button
            key={item.service_id}
            type="button"
            className={`r521-service-option${service?.service_id === item.service_id ? ' selected' : ''}`}
            onClick={() => onService(item)}
          >
            <span className="code">{item.display_code}</span>
            <strong>{item.title}</strong>
            <p>{item.description}</p>
            <span className="go">Выбрать →</span>
          </button>
        ))}
      </div>
    </section>
  </>
);

const MaterialsStep: React.FC<{
  files: LocalFile[];
  blockedFile: string | null;
  fileQuestions: ResearchQuestion[];
  inputRef: React.RefObject<HTMLInputElement>;
  onPick: () => void;
  onFiles: (files: File[]) => void;
  onReplace: (index: number) => void;
  onDelete: (index: number) => void;
}> = ({ files, blockedFile, fileQuestions, inputRef, onPick, onFiles, onReplace, onDelete }) => (
  <>
    <section className="r54-panel">
      <div
        className="r54-dropzone"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          onFiles(Array.from(event.dataTransfer.files));
        }}
      >
        <div>
          <span className="r54-doc-icon">Загрузка</span>
          <h2>Перетащите материалы или выберите файлы</h2>
          <p>PDF, DOCX, XLSX, PPTX, JPG, PNG, CSV, TXT, ZIP. Не принимаются EXE, MSI, BAT, CMD, JS и другие исполняемые форматы.</p>
          {fileQuestions.length ? <p>Вопросы услуги с файлами: {fileQuestions.map((item) => item.label).join(' · ')}</p> : null}
          <button type="button" className="r54-action" onClick={onPick}>Выбрать файлы</button>
          <input
            ref={inputRef}
            type="file"
            hidden
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.csv,.txt,.zip"
            onChange={(event) => {
              onFiles(Array.from(event.target.files || []));
              event.target.value = '';
            }}
          />
        </div>
      </div>
    </section>
    <section className="r54-panel">
      <header>
        <div>
          <h2>Загруженные материалы</h2>
          <p>{files.length} из {MAX_FILES} файлов</p>
        </div>
        <span className="r54-chip good">Прикреплён</span>
      </header>
      <div className="r54-list">
        {files.map((file, index) => (
          <div key={file.file_id} className="r54-doc">
            <span className="r54-doc-icon">{file.type}</span>
            <div>
              <strong>{file.name}</strong>
              <small>{formatSize(file.size)} · {file.status} · проверка формата пройдена</small>
            </div>
            <div className="r54-card-actions">
              <button type="button" onClick={() => onReplace(index)}>Заменить</button>
              <button type="button" onClick={() => onDelete(index)}>Удалить</button>
            </div>
          </div>
        ))}
        {blockedFile ? (
          <div className="r54-doc">
            <span className="r54-doc-icon" style={{ borderColor: '#ff7a7a', color: '#ff7a7a' }}>EXE</span>
            <div>
              <strong>{blockedFile}</strong>
              <small>Формат отклонён · исполняемые и неподдерживаемые файлы запрещены</small>
            </div>
            <span className="r54-chip bad">Недоступно</span>
          </div>
        ) : null}
      </div>
    </section>
  </>
);

const ReviewStep: React.FC<{
  service: GatewayService;
  direction: GatewayDirection | null;
  counts: { SOURCE_CONFIRMED: number; NEEDS_CONFIRMATION: number; USER_CONFIRMED: number; CONFLICT_REVIEW: number; changed: number };
  recommendations: string[];
  missing: string[];
  declaration: boolean;
  submitted: ResearchSubmitResult | null;
  onDeclaration: (value: boolean) => void;
}> = ({ service, direction, counts, recommendations, missing, declaration, submitted, onDeclaration }) => (
  <>
    <section className="r55-review-summary">
      <article className="r54-stat"><small>Принятые</small><b>{counts.SOURCE_CONFIRMED + counts.USER_CONFIRMED}</b><span>Ответы подтверждены</span></article>
      <article className="r54-stat" style={{ ['--r54-accent' as string]: '#b48cff' }}><small>Изменённые</small><b>{counts.changed}</b><span>Исправлено пользователем</span></article>
      <article className="r54-stat" style={{ ['--r54-accent' as string]: 'var(--r54-gold)' }}><small>Неподтверждённые</small><b>{counts.NEEDS_CONFIRMATION}</b><span>Требует подтверждения</span></article>
      <article className="r54-stat" style={{ ['--r54-accent' as string]: '#ff7a7a' }}><small>Конфликты</small><b>{counts.CONFLICT_REVIEW}</b><span>Требует уточнения</span></article>
    </section>
    <section className="r54-grid-2">
      <article className="r54-panel">
        <header>
          <div>
            <h2>Услуга и клиент</h2>
            <p>Выбранная услуга и направление.</p>
          </div>
          <span className="r54-chip good">{service.display_code}</span>
        </header>
        <div className="r54-list">
          <div className="r54-row">
            <span className="r54-doc-icon">↗</span>
            <div><strong>{direction?.title}</strong><small>Направление</small></div>
            <span className="r54-chip info">Выбрано</span>
          </div>
          <div className="r54-row">
            <span className="r54-doc-icon">✓</span>
            <div><strong>{service.title}</strong><small>Услуга {service.display_code}</small></div>
            <span className="r54-chip info">Услуга</span>
          </div>
        </div>
      </article>
      <article className="r54-panel">
        <header>
          <div>
            <h2>Проверка перед отправкой</h2>
            <p>{submitted ? submitted.reference_code : 'Заявка создаётся в реестре исследований.'}</p>
          </div>
        </header>
        {missing.length ? (
          <div className="r54-list">
            {missing.slice(0, 8).map((item) => <p key={item} className="r54-note">{item}</p>)}
          </div>
        ) : <p className="r54-note">Обязательные поля выбранной услуги заполнены.</p>}
        {recommendations.length ? (
          <div className="r54-list">
            {recommendations.map((item) => <p key={item} className="r54-note">{item}</p>)}
          </div>
        ) : null}
        <label className="r54-declare">
          <input type="checkbox" checked={declaration} onChange={(event) => onDeclaration(event.target.checked)} />
          <span>Подтверждаю, что сведения и материалы относятся к услуге «{service.title}».</span>
        </label>
      </article>
    </section>
  </>
);
